import React, { useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { vehicleContext } from '../VehicleProvider';
import type { VehicleDetails } from '../../types';
import TechnicalDataModal from './TechnicalDataModal';
import GearsIcon from '../../assets/icons/GearsIcon';
import FallbackCarDefault from '../../assets/images/fallback_car_default.png';
import {
  InternalColour,
  ColourSwatch,
  External,
  ExternalColour,
  BodyImage,
  LegalCopy,
  Wrapper,
  ColourWrap,
  Internal,
  Colours,
  TitleWrap,
  IconWrap,
  Body,
  Aspect,
  BodyImageWrap,
  Mechanics,
  TextWrap,
  ViewTechSpecsIconWrapper,
} from './OverviewContent.styles';
import Carfax from '../Carfax';
import { Icon, Layout, Text, TextLink } from '@oneaudi/unified-web-components';
import { SYS_BREAKPOINT_MD, SYS_SIZE_FIXED_250 } from '@oneaudi/unified-web-common';

interface OverviewContentProps {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const OverviewContent: React.FC<OverviewContentProps> = ({ modalOpen, setModalOpen }) => {
  const { vehicle, market } = useContext(vehicleContext);
  const { t } = useTranslation();
  const isMobile = useMediaQuery({
    query: `(max-width: ${SYS_BREAKPOINT_MD})`,
  });
  const isUsedCAN = market === 'cauc';
  const isUsedUS = market === 'usuc';

  const onModalOpen = useCallback(() => {
    setModalOpen(true);
  }, [setModalOpen]);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const {
    interiorTileImage,
    exteriorTileImage,
    exteriorColor,
    upholsteryColor,
    bodyType,
    gearType,
    driveType,
    fuelType,
    vehicleMedia,
    modelName,
  } = vehicle as VehicleDetails;

  if (!vehicle) return null;

  const { mediaImages } = vehicleMedia;

  const bodyImage =
    mediaImages?.find((image) => image.imageType === 'side_facing')?.url || FallbackCarDefault;

  return (
    <>
      <Wrapper data-testid="overview-wrapper">
        {(exteriorTileImage || interiorTileImage) && (
          <Colours>
            <TitleWrap>
              <IconWrap>
                <Icon name="car-search" size="s" />
              </IconWrap>
              <Text
                tag="p"
                variant={{
                  order: '2',
                  type: 'copy',
                  style: 'bold',
                }}
              >
                {market === 'ca' || market === 'cauc'
                  ? t('featuresAndSpecsSection.categories.overview.headers.colourCA')
                  : t('featuresAndSpecsSection.categories.overview.headers.colourUS')}
              </Text>
            </TitleWrap>
            <ColourWrap data-testid="internal-and-external-colors-wrapper">
              {exteriorTileImage && (
                <External margin={!!interiorTileImage}>
                  <ExternalColour data-testid="exterior-colour">
                    <ColourSwatch
                      src={exteriorTileImage}
                      alt={`${modelName} exterior color ${exteriorColor}`}
                    />
                  </ExternalColour>
                  <Text
                    variant={{
                      order: '2',
                      type: 'copy',
                      style: 'normal',
                    }}
                    data-vehicle-exterior-color={exteriorColor}
                    data-testid="exterior-colour-text"
                  >
                    {exteriorColor}
                  </Text>
                </External>
              )}
              {interiorTileImage && (
                <Internal data-testid="internal-color-text-and-image-wrapper">
                  <InternalColour data-testid="interior-colour">
                    <ColourSwatch src={interiorTileImage} alt={`${modelName} interior image`} />
                  </InternalColour>
                  {upholsteryColor && (
                    <Text
                      variant={{
                        order: '2',
                        type: 'copy',
                        style: 'normal',
                      }}
                      data-vehicle-interior-color-seats={upholsteryColor}
                      data-testid="interior-colour-text"
                    >
                      {upholsteryColor}
                    </Text>
                  )}
                </Internal>
              )}
            </ColourWrap>
          </Colours>
        )}
        <Body data-testid="body-type-image-legalcopy-wrapper">
          {bodyType && (
            <Aspect data-testid="body-type-icon-and-text-wrapper">
              <IconWrap>
                <Icon name="traffic" size="s" />
              </IconWrap>
              <Layout
                direction={'column'}
                className="bodyTypeTitleWrapper"
                data-testid="body-type-title-and-subtitle-wrapper"
              >
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                >
                  {t('featuresAndSpecsSection.categories.overview.headers.bodyType')}
                </Text>
                <Text
                  variant={{
                    type: 'copy',
                    order: '2',
                    style: 'short',
                  }}
                  data-vehicle-body-type={bodyType}
                  data-testid="body-type"
                >
                  {bodyType}
                </Text>
              </Layout>
            </Aspect>
          )}
          <BodyImageWrap
            data-vehicle-model-name={modelName}
            className="bodyImgDiv"
            data-testid="body-img"
          >
            {bodyImage && (
              <BodyImage src={isMobile ? `${bodyImage}?wid=1000` : bodyImage} alt={modelName} />
            )}
          </BodyImageWrap>
          {isUsedCAN && (
            <LegalCopy>
              {t('featuresAndSpecsSection.categories.overview.headers.legalCopy')}
            </LegalCopy>
          )}
          {isUsedUS && (
            <LegalCopy>
              {t('featuresAndSpecsSection.categories.overview.headers.legalCopyUS')}
            </LegalCopy>
          )}
        </Body>
        <Mechanics data-testid="fuel-gear-drivetrain-tech-carfax-wrapper">
          {fuelType && (
            <Aspect data-testid="fuel-icon-and-texts-wrapper">
              <IconWrap>
                {fuelType === 'Electric' ? (
                  <Icon name="charging-station" size="s" />
                ) : (
                  <Icon name="fuel-station" size="s" />
                )}
              </IconWrap>
              <TextWrap data-testid="body-type-title-and-subtitle-wrapper">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  data-testid="fuel-type"
                >
                  {t('featuresAndSpecsSection.categories.overview.headers.fuelType')}
                </Text>
                <Text
                  variant={{
                    type: 'copy',
                    order: '2',
                    style: 'short',
                  }}
                  data-testid="fuel-type-text"
                  data-vehicle-fuel={fuelType}
                >
                  {fuelType}
                </Text>
              </TextWrap>
            </Aspect>
          )}
          {gearType && (
            <Aspect data-testid="gears-icon-and-texts-wrapper">
              <IconWrap>
                <GearsIcon />
              </IconWrap>
              <TextWrap>
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  data-testid="gear-type"
                >
                  {t('featuresAndSpecsSection.categories.overview.headers.gearType')}
                </Text>
                <Text
                  variant={{
                    type: 'copy',
                    order: '2',
                    style: 'short',
                  }}
                  data-testid="gear-type-text"
                  data-vehicle-gear={gearType}
                >
                  {gearType}
                </Text>
              </TextWrap>
            </Aspect>
          )}
          {driveType && (
            <Aspect data-testid="drive-train-icon-and-texts-wrapper">
              <IconWrap>
                <Icon name="quattro" size="s" />
              </IconWrap>
              <TextWrap data-testid="drive-train-title-and-subtitle-wrapper">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  data-testid="drive-type"
                >
                  {t('featuresAndSpecsSection.categories.overview.headers.drivetrain')}
                </Text>
                <Text
                  variant={{
                    type: 'copy',
                    order: '2',
                    style: 'short',
                  }}
                  data-testid="drive-type-text"
                  data-vehicle-drivetrain={driveType}
                >
                  {driveType} p
                </Text>
              </TextWrap>
            </Aspect>
          )}
          <Aspect>
            <IconWrap>
              <TextLink
                id="infoIconVDPFSS"
                data-testid="technical-information-modal-button"
                aria-label={t('featuresAndSpecsSection.ariaLabel.technicalInformation')}
                className="className"
                href="#"
                iconPosition="leading"
                margin={[0, 0, 0, SYS_SIZE_FIXED_250]}
                onClick={onModalOpen}
                rel="noopener"
                size="sm"
                target="_self"
                title="Title"
                variant="standalone"
              >
                <ViewTechSpecsIconWrapper>
                  <Icon name="inspection" size="s" />
                </ViewTechSpecsIconWrapper>
                {t('featuresAndSpecsSection.categories.overview.headers.technicalSpecsCta')}
              </TextLink>
            </IconWrap>
          </Aspect>
          {!isMobile && (
            <Carfax isCanadian={isUsedCAN} vehicleHistoryReports={vehicle.vehicleHistoryReports} />
          )}
        </Mechanics>
        {!!isMobile && (
          <Carfax isCanadian={isUsedCAN} vehicleHistoryReports={vehicle.vehicleHistoryReports} />
        )}
      </Wrapper>
      {modalOpen && <TechnicalDataModal onClose={onModalClose} />}
    </>
  );
};

export default OverviewContent;
